import { useTranslation } from 'react-i18next';

import { Box, createUseVariantStyle, rem, StyleProps, Tooltip } from '@bonusly/design-system';

import { splitOverflow } from '../../utils/array';

interface UserAvatarExcessProps extends StyleProps {
	count: number;
	size: string;
	userNames: string[];
}

const useVariantStyle = createUseVariantStyle({
	size: {
		sm: {
			fontSize: 'sm',
			boxSize: rem(32),
		},
		md: {
			fontSize: 'md',
			boxSize: rem(40),
		},
		lg: {
			fontSize: 'md',
			boxSize: rem(48),
		},
		xl: {
			fontSize: 'lg',
			boxSize: rem(56),
		},
		'2xl': {
			fontSize: 'xl',
			boxSize: rem(72),
		},
	},
});

export const UserAvatarExcess: React.FC<UserAvatarExcessProps> = ({ count, size, userNames, ...rest }: UserAvatarExcessProps) => {
	const { t } = useTranslation('common');

	const { head: names, overflowCount: extra } = splitOverflow(userNames, {
		max: 10,
		minOverflow: 2,
	});

	return (
		<Tooltip
			openDelay={500}
			label={t('intl_overflow_list', {
				names,
				count: extra,
			})}
		>
			<Box
				alignItems="center"
				as="span"
				backgroundColor="gray.50"
				sx={{
					outlineWidth: '1px',
					outlineColor: 'white',
					outlineStyle: 'solid',
					fontWeight: 'medium',
				}}
				borderRadius="full"
				display="inline-flex"
				justifyContent="center"
				lineHeight={1}
				pos="relative"
				transitionDuration="normal"
				transitionProperty="common"
				_hover={{
					bgColor: 'gray.600',
					color: 'white',
					cursor: 'default',
				}}
				{...useVariantStyle({ size })}
				{...rest}
			>
				+{count}
			</Box>
		</Tooltip>
	);
};
